import {
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LoggedUserService } from "@keus-template/auth";
import { EMPTY, Subject, switchMap, takeUntil } from "rxjs";
import { ResponseClinicDto, ResponseTestDto } from "../../api/models";
import { BookingStatus } from "../../enums";
import { PrescriptionPaymentType } from "../../enums/payment-type.enum";
import { PrescriptionType } from "../../enums/prescription-type.enum";
import { BookingPrescriptionState } from "../../interfaces/booking-prescription-state.interface";
import { CachedClinicsService } from "../cache";
import { CachedTestsService } from "../cache/healthcare/cached-tests.service";
import { PathwaysApiService } from "../api/treatment/pathways-api.service";
import { PathwayType } from "../../enums/pathways.enum";

/**
 * Payment status constants for prescription booking
 * @const
 */
const PAYMENT_STATUS = {
  PAID: 'paid',
  SCHEDULED: 'scheduled',
} as const;

/** Type for payment status */
type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

@Injectable({
  providedIn: "root",
})
export class BookingPrescriptionStateService {
  /** The cached clinics service instance  */
  private readonly _cachedClinicsService = inject(CachedClinicsService);

  /** The activate route instance  */
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);

  /** The router instance */
  private readonly _router: Router = inject(Router);

  /** The logged user service instance */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** The cached prescriptions service instance */
  private readonly _cachedTestsService: CachedTestsService =
    inject(CachedTestsService);

  /** The pathways API service instance */
  private readonly _pathwaysApiService = inject(PathwaysApiService);

  /** 
   * Cached pathway type for performance optimization
   * Avoids repeated API calls during the booking flow
   */
  private _pathwayType: PathwayType | null = null;

  /** Subject for managing subscriptions and cleanup */
  private readonly _destroy$ = new Subject<void>();

  /** The state of the booking */
  private readonly _state: WritableSignal<BookingPrescriptionState> =
    signal<BookingPrescriptionState>({
      type: null,
      currentStep: 1,
      totalSteps: 0,
    });

  /** Public readonly version of the state */
  public readonly state: Signal<BookingPrescriptionState> =
    this._state.asReadonly();

  private readonly _error: WritableSignal<any | null> = signal(null);

  public readonly error: Signal<any | null> = this._error.asReadonly();

  private readonly _processCompleted: WritableSignal<boolean> = signal(false);

  public readonly processCompleted: Signal<boolean> =
    this._processCompleted.asReadonly();

  /**
   * Initialize a new prescription session
   * Should be called in the component's OnInit lifecycle hook
   * 
   * Initialization sequence:
   * 1. Set patient data from logged user
   * 2. Load pathway type for Fertility Check-in detection
   * 3. Calculate total steps based on prescription type
   * 4. Load prescription data if ID is provided
   */
  public initPrescription(): void {
    this._setPatientData();
    this._loadPathwayType();

    const prescriptionType = this._route.snapshot.queryParamMap.get("type");
    const prescriptionId = this._route.snapshot.queryParamMap.get("id");

    if (prescriptionType) {
      this._calculateTotalStepsByType(prescriptionType as PrescriptionType);
    }

    if (prescriptionType === PrescriptionType.SELECT_TEST) {
      this._state.update((s) => ({
        ...s,
      }));
      return;
    }

    if (
      prescriptionId &&
      (prescriptionType === PrescriptionType.REPROGRAMMING ||
        prescriptionType === PrescriptionType.DIRECT)
    ) {
      this._loadPrescriptionById(prescriptionId, prescriptionType);
      return;
    }

    this._router.navigate(["/my-prescriptions"], {
      queryParams: { error: "prescription-id-missing" },
    });
  }

  /**
   * Load a prescription by id.
   * @param prescriptionId the id of the prescription
   */
  private _loadPrescriptionById(
    prescriptionId: string,
    type: PrescriptionType,
  ): void {
    this._cachedTestsService.findOne$(prescriptionId).pipe(
      switchMap((prescription) => {
        if (!prescription || prescription.status === BookingStatus.COMPLETED) {
          this._router.navigate(["/my-prescriptions"], {
            queryParams: { error: "prescription-not-found" },
          });
          return EMPTY;
        }
        this.setTest(prescription, type);
        return this._cachedClinicsService.findOne$(prescription.clinicId!);
      })
    ).subscribe((clinic) => {
      this._setClinicData(clinic);
    });
  }

  /**
   * Sets the test data in the state from a ResponseTestDto.
   * @param test The test DTO containing prescription data
   * @param type The type of the prescription
   */
  private _setTestFromDto(test: ResponseTestDto, type: PrescriptionType): void {
    // Handle UNBOOKED placeholder value - treat it as no booking date
    const bookingDate = test.bookingDate === 'UNBOOKED' ? undefined : test.bookingDate;

    this._state.update((s) => ({
      ...s,
      type,
      doctorId: test.doctorId,
      doctorFirstName: test.doctorFirstName,
      doctorLastName: test.doctorLastName,
      bookingDate: bookingDate,
      prescriptionId: test.id,
      title: test.title,
      calendarId: test.calendarId,
      testId: test.id,
      clinicId: test.clinicId,
      clinicName: test.clinicName,
      clinicEmail: test.clinicEmail,
      price: test.price,
      paymentStatus: test.paymentStatus,
      pathwayId: test.pathwayId,
      isExternal: test.isExternal,
    }));
  }

  /**
   * Sets the clinic data in the state from a ResponseClinicDto.
   * @param clinic The clinic DTO containing clinic data
   */
  private _setClinicData(clinic: ResponseClinicDto): void {
    this._state.update((s) => ({
      ...s,
      clinicProfilePic: clinic.profilePic,
    }));
  }

  /**
   * Set the patient data
   */
  private _setPatientData(): void {
    const user = this._loggedUserService.currentUser();

    if (user) {
      this._state.update((s) => ({
        ...s,
        patientId: user.id,
        patientFirstName: user.firstName,
        patientLastName: user.lastName,
        patientEmail: user.email,
      }));
    } else {
      this._router.navigate(["/my-prescriptions"], {
        queryParams: { error: "user-not-found" },
      });
    }
  }

  /**
   * Sets the test in the state using the provided test DTO and prescription type.
   * @param test The test DTO containing prescription data
   * @param type The type of the prescription (defaults to SELECT_TEST)
   */
  public setTest(
    test: ResponseTestDto,
    type: PrescriptionType = PrescriptionType.SELECT_TEST,
  ): void {
    this._setTestFromDto(test, type);
  }

  /**
   * Set only the date part of the test, time is reset
   * @param dateOnly A Date object containing year, month, day
   */
  public setBookingDate(dateOnly: Date): void {
    const newDate = new Date(dateOnly);
    newDate.setHours(0, 0, 0, 0);
    this._state.update((s) => ({
      ...s,
      bookingDate: newDate.toISOString(),
    }));
  }

  /**
   * Set only the time of the existing test date
   * @param time A string in "HH:mm" format
   */
  public setBookingTime(time: string): void {
    this._state.update((s) => {
      if (!s.bookingDate) return s;

      const [hours, minutes] = time.split(":").map(Number);
      const current = new Date(s.bookingDate);
      current.setHours(hours, minutes, 0, 0);

      return {
        ...s,
        bookingDate: current.toISOString(),
      };
    });
  }

  /**
   * Set payment information
   * @param payment the type of the payment
   * @param price the price of the prescription
   */
  public setPayment(payment: PrescriptionPaymentType, price: number): void {
    this._state.update((s) => ({ ...s, payment, price }));
  }

  /**
   * Set selected external clinic
   * @param clinicId the id of the selected external clinic
   */
  public setExternalClinic(clinicId: string): void {
    this._state.update((s) => ({ ...s, selectedExternalClinicId: clinicId }));
    // Recalculate steps after clinic selection
    const currentType = this._state().type;
    if (currentType) {
      this._calculateTotalStepsByType(currentType);
    }
  }

  /**
   * Update clinic data in state (for external clinics)
   * @param clinic the full clinic data
   */
  public updateClinicData(clinic: ResponseClinicDto): void {
    this._state.update((s) => ({
      ...s,
      clinicId: clinic.id,
      clinicName: clinic.name,
      clinicEmail: clinic.email,
    }));
  }

  public setError(error: any): void {
    this._error.set(error);
  }

  public setCompletedProcess(value: boolean): void {
    this._processCompleted.set(value);
  }

  /**
   * Move to next step if possible
   */
  public nextStep(): void {
    this._state.update((s) => ({
      ...s,
      currentStep: Math.min(s.currentStep + 1, s.totalSteps),
    }));
  }

  /**
   * Move to previous step if possible
   */
  public prevStep(): void {
    this._state.update((s) => ({
      ...s,
      currentStep: Math.max(s.currentStep - 1, 1),
    }));
  }

  /**
   * Check if the user has setter hours
   * @returns true if has setted hours, false otherwise
   */
  public hasSettedHours(): boolean {
    const bookingDate = this._state().bookingDate;
    if (!bookingDate || bookingDate === 'UNBOOKED') return false;

    const date = new Date(bookingDate);
    return !(date.getHours() === 0 && date.getMinutes() === 0);
  }

  /**
   * Returns the action type for the current prescription.
   * @returns "update" if the type is REPROGRAMMING, otherwise "create"
   */
  public getActionType(): "create" | "update" {
    const state = this._state();
    return state.type === PrescriptionType.REPROGRAMMING ? "update" : "create";
  }

  /**
   * Reset prescription state and cleanup subscriptions
   * Should be called when leaving the booking flow or on component destroy
   */
  public resetPrescription(): void {
    // Reset state to initial values
    this._state.set({
      type: null,
      currentStep: 1,
      totalSteps: 0,
    });

    // Clear error and completion flags
    this._error.set(null);
    this._processCompleted.set(false);
    
    // Clear cached data
    this._pathwayType = null;
    
    // Unsubscribe from active subscriptions
    this._destroy$.next();
  }

  /**
   * Loads pathway type for current user
   * Used to detect Fertility Check-in pathways and adjust booking flow
   * Automatically recalculates steps when pathway type is loaded
   */
  private _loadPathwayType(): void {
    const userId = this._loggedUserService.currentUser()?.id;
    if (!userId) return;

    this._pathwaysApiService.findActiveByPatientId$(userId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (pathway) => {
          if (pathway?.pathwayType) {
            this._pathwayType = pathway.pathwayType as PathwayType;
            // Recalculate steps if already initialized (dynamic step adjustment)
            const currentType = this._state().type;
            if (currentType) {
              this._calculateTotalStepsByType(currentType);
            }
          }
        },
        error: (error) => {
          console.error('[BookingPrescriptionState] Failed to load pathway type:', error);
          // Continue without pathway type - will use default flow
        }
      });
  }

  /**
   * Checks if test is pre-paid (Fertility Check-in package)
   * Pre-paid tests skip the payment step in the booking flow
   * 
   * @returns true if test is part of a pre-paid Fertility Check-in package
   */
  private _isPrePaidTest(): boolean {
    const state = this._state();
    const isFertilityCheckIn = 
      this._pathwayType === PathwayType.FERTILITY_CHECKIN_MALE ||
      this._pathwayType === PathwayType.FERTILITY_CHECKIN_FEMALE;
    
    return isFertilityCheckIn && state.paymentStatus === PAYMENT_STATUS.PAID;
  }

  /**
   * Checks if current test is external
   * @returns true if test is external, false otherwise
   */
  private _isExternalTest(): boolean {
    const state = this._state();
    return state.isExternal === true;
  }

  /**
   * Calculates and sets the total steps by the type of the prescription
   * 
   * Step count by type:
   * - SELECT_TEST: 4 steps normally, 5 for external tests (select, clinic, date, payment, confirmation)
   * - DIRECT: 3 steps normally, 4 for external tests, 2/3 for pre-paid tests (clinic, date, payment, confirmation)
   * - REPROGRAMMING: 2 steps normally, 3 for external tests (clinic, date, confirmation)
   * 
   * @param type the type of the prescription
   */
  private _calculateTotalStepsByType(type: PrescriptionType): void {
    let totalSteps = 0;
    const isExternal = this._isExternalTest();

    switch (type) {
      case PrescriptionType.SELECT_TEST:
        totalSteps = isExternal ? 5 : 4; // (select -> clinic -> date -> payment -> confirmation) OR (select -> date -> payment -> confirmation)
        break;
      case PrescriptionType.DIRECT:
        if (isExternal) {
          // External: clinic -> date -> payment -> confirmation OR clinic -> date -> confirmation (if pre-paid)
          totalSteps = this._isPrePaidTest() ? 3 : 4;
        } else {
          // Internal: date -> payment -> confirmation OR date -> confirmation (if pre-paid)
          totalSteps = this._isPrePaidTest() ? 2 : 3;
        }
        break;
      case PrescriptionType.REPROGRAMMING:
        totalSteps = isExternal ? 3 : 2; // (clinic -> date -> confirmation) OR (date -> confirmation)
        break;
      default:
        totalSteps = 1;
    }

    if (totalSteps > 0) {
      this._state.update((s) => ({
        ...s,
        totalSteps,
        type,
      }));
    }
  }

  /**
   * Returns the key for the current step based on type and step number
   * Used to determine which component to display in the booking flow
   * 
   * Step mapping (external tests have additional clinic step):
   * - SELECT_TEST: select-test -> [clinic] -> date -> payment -> confirmation
   * - DIRECT: [clinic] -> date -> (payment OR confirmation) -> confirmation
   * - REPROGRAMMING: [clinic] -> date -> confirmation
   * 
   * @returns the current step key or empty string if invalid
   */
  public getCurrentStepKey(): string {
    const state = this._state();
    const { type, currentStep } = state;

    if (!type) return "";

    const isExternal = this._isExternalTest();

    // Step mappings by prescription type
    const stepMappings: Record<PrescriptionType, Record<number, string>> = {
      [PrescriptionType.SELECT_TEST]: isExternal ? {
        1: "select-test",
        2: "clinic",
        3: "date",
        4: "payment",
        5: "confirmation",
      } : {
        1: "select-test",
        2: "date",
        3: "payment",
        4: "confirmation",
      },
      [PrescriptionType.DIRECT]: isExternal ? {
        1: "clinic",
        2: "date",
        3: this._isPrePaidTest() ? "confirmation" : "payment",
        4: "confirmation",
      } : {
        1: "date",
        2: this._isPrePaidTest() ? "confirmation" : "payment",
        3: "confirmation",
      },
      [PrescriptionType.REPROGRAMMING]: isExternal ? {
        1: "clinic",
        2: "date",
        3: "confirmation",
      } : {
        1: "date",
        2: "confirmation",
      },
    };

    return stepMappings[type]?.[currentStep] || "";
  }

  /**
   * Checks if the current step has valid data
   * Used to enable/disable navigation to next step
   * 
   * Validation rules:
   * - select-test: Test must be selected
   * - clinic: Clinic must be selected
   * - date: Date and time must be set (not 'UNBOOKED')
   * - payment: Payment method must be selected
   * - confirmation: Always valid (review step)
   * 
   * @returns true if current step data is valid, false otherwise
   */
  public isCurrentStepValid(): boolean {
    const stepKey = this.getCurrentStepKey();
    const state = this._state();

    const validators: Record<string, () => boolean> = {
      "select-test": () => !!state.testId,
      "clinic": () => !!state.selectedExternalClinicId,
      "date": () => {
        const hasValidDate = !!state.bookingDate && state.bookingDate !== 'UNBOOKED';
        const hasValidTime = this.hasSettedHours();
        return hasValidDate && hasValidTime;
      },
      "payment": () => !!state.payment,
      "confirmation": () => true, // Always valid for review step
    };

    return validators[stepKey]?.() ?? false;
  }
}
